import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Card, 
  Button, 
  Space, 
  Table, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  Popconfirm,
  Tag,
  Tooltip,
  Switch,
  Row,
  Col
} from 'antd';
import { 
  ApartmentOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  RobotOutlined
} from '@ant-design/icons';
import { topicTreeService } from '../../services/topicTreeService';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const TopicTreeManagement = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [form] = Form.useForm();

  // 获取topic列表
  const fetchTopics = async () => {
    try {
      setLoading(true);
      console.log('开始获取topic列表...');
      const response = await topicTreeService.getAllTopics();
      console.log('API响应:', response);
      
      // 正确提取数据：response.data.data 才是真正的数组
      const data = response.data?.data || [];
      console.log('处理后的数据:', data);
      console.log('数据类型:', typeof data, '是否为数组:', Array.isArray(data));
      
      const finalData = Array.isArray(data) ? data : [];
      setTopics(finalData);
      console.log('设置到state的数据:', finalData);
    } catch (error) {
      console.error('获取topic列表失败:', error);
      console.error('错误详情:', error.response);
      message.error('Failed to fetch topic list: ' + (error.response?.data?.message || error.message));
      // 确保出错时也设置为空数组
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载数据
  useEffect(() => {
    fetchTopics();
  }, []);

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'tnl_id',
      key: 'tnl_id',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Topic Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Tag color="blue">{text}</Tag>
          {record.level > 0 && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Level {record.level}
            </Text>
          )}
        </Space>
      ),
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value, record) =>
        record.name.toLowerCase().includes(value.toLowerCase()) ||
        (record.description && record.description.toLowerCase().includes(value.toLowerCase())),
    },
    {
      title: 'Parent ID',
      dataIndex: 'super_tnl_id',
      key: 'super_tnl_id',
      width: 150,
      ellipsis: true,
      render: (super_tnl_id) => super_tnl_id || '-',
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: 80,
      render: (level) => (
        <Tag color={level === 0 ? 'red' : level === 1 ? 'orange' : 'green'}>
          {level}
        </Tag>
      ),
      sorter: (a, b) => a.level - b.level,
    },
    {
      title: 'Private',
      dataIndex: 'is_private',
      key: 'is_private',
      width: 100,
      render: (is_private) => (
        <Tag color={is_private ? 'red' : 'green'}>
          {is_private ? 'Private' : 'Public'}
        </Tag>
      ),
      filters: [
        { text: 'Public', value: false },
        { text: 'Private', value: true },
      ],
      onFilter: (value, record) => record.is_private === value,
    },
    {
      title: 'Permanent',
      dataIndex: 'is_forever',
      key: 'is_forever',
      width: 100,
      render: (is_forever) => (
        <Tag color={is_forever ? 'purple' : 'default'}>
          {is_forever ? 'Permanent' : 'Normal'}
        </Tag>
      ),
      filters: [
        { text: 'Normal', value: false },
        { text: 'Permanent', value: true },
      ],
      onFilter: (value, record) => record.is_forever === value,
    },
    {
      title: 'Path',
      dataIndex: 'path',
      key: 'path',
      ellipsis: {
        showTitle: false,
      },
      render: (path) => (
        <Tooltip placement="topLeft" title={path}>
          <Text code style={{ fontSize: '12px' }}>{path}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: {
        showTitle: false,
      },
      render: (description) => (
        <Tooltip placement="topLeft" title={description}>
          {description || '-'}
        </Tooltip>
      ),
    },
    {
      title: 'Tag List',
      dataIndex: 'tagnamelist',
      key: 'tagnamelist',
      ellipsis: {
        showTitle: false,
      },
      render: (tagnamelist) => (
        <Tooltip placement="topLeft" title={tagnamelist}>
          {tagnamelist || '-'}
        </Tooltip>
      ),
    },

    {
      title: 'Actions',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Confirm Delete"
            description={`Are you sure you want to delete topic "${record.name}"?`}
                          onConfirm={() => handleDelete(record.tnl_id)}
            okText="Confirm"
            cancelText="Cancel"
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 搜索处理
  const handleSearch = (value) => {
    setSearchText(value);
  };

  // 添加新主题
  const handleAdd = () => {
    setEditingTopic(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  // 编辑主题
  const handleEdit = (topic) => {
    setEditingTopic(topic);
    form.setFieldsValue({
      name: topic.name,
      super_tnl_id: topic.super_tnl_id,
      description: topic.description,
      tagnamelist: topic.tagnamelist,
      is_private: topic.is_private,
      is_forever: topic.is_forever || false
    });
    setIsModalVisible(true);
  };

  // 删除单个主题
  const handleDelete = async (tnl_id) => {
    try {
      await topicTreeService.deleteTopic(tnl_id);
      message.success('Deleted successfully');
      fetchTopics();
    } catch (error) {
      console.error('删除失败:', error);
      message.error('Delete failed: ' + (error.response?.data?.message || error.message));
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select records to delete');
      return;
    }

    Modal.confirm({
      title: 'Confirm Batch Delete',
      content: `Are you sure you want to delete the selected ${selectedRowKeys.length} records?`,
      okText: 'Confirm Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await topicTreeService.deleteMultipleTopics(selectedRowKeys);
          message.success(`Successfully deleted ${selectedRowKeys.length} records`);
          setSelectedRowKeys([]);
          fetchTopics();
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('Batch delete failed: ' + (error.response?.data?.message || error.message));
        }
      }
    });
  };

  // 表单提交
  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingTopic) {
        // 更新主题
        await topicTreeService.updateTopic(editingTopic.tnl_id, values);
        message.success('Updated successfully');
      } else {
        // 创建新主题
        await topicTreeService.createTopic(values);
        message.success('Created successfully');
      }
      
      setIsModalVisible(false);
      fetchTopics();
    } catch (error) {
      if (error.errorFields) {
        message.error('Please check form input');
        return;
      }

            console.error('保存失败:', error);
      message.error('Save failed: ' + (error.response?.data?.message || error.message));
    }
  };

  // AI生成标签函数
  const handleAIGenerateTags = async () => {
    try {
      setAiGenerating(true);
      
      // 获取当前表单的主题名称和描述
      const currentValues = form.getFieldsValue(['name', 'description']);
      
      if (!currentValues.name || !currentValues.description) {
        message.warning('Please fill in the topic name and description first for AI tag recommendations');
        return;
      }
      
      // 调用AI标签推荐服务
      const response = await topicTreeService.recommendTopicTags({
        topicName: currentValues.name,
        description: currentValues.description
      });
      
      console.log('AI推荐标签响应:', response);
      
      // 自动填充推荐的标签
      // 注意：后端返回的数据结构是 { data: { data: { tags: string }, message: string } }
      const tags = response.data?.data?.tags;
      if (tags) {
        form.setFieldsValue({
          tagnamelist: tags
        });
        message.success('AI tag recommendation successful!');
        console.log('已填充标签到表单:', tags);
      } else {
        console.error('响应中没有找到tags字段:', response);
        message.error('AI returned tags in unexpected format');
      }
    } catch (error) {
      console.error('AI推荐标签失败:', error);
      message.error('AI tag recommendation failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setAiGenerating(false);
    }
  };

  // 表格行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      name: record.name,
    }),
  };

  // 获取可作为父级的主题选项
  const getParentOptions = () => {
    // 确保topics是数组
    if (!Array.isArray(topics)) {
      return [];
    }
    
    return topics
      .filter(topic => 
        topic.level === 0 && // 只显示level 0的主题
        (!editingTopic || topic.tnl_id !== editingTopic.tnl_id)
      )
      .map(topic => ({
        value: topic.tnl_id,
        label: `${topic.name} (Level ${topic.level || 0})`
      }));
  };

  return (
    <div className="admin-module">
      <div className="admin-module-header">
        <Title level={3} className="admin-module-title">Topic Tree Management</Title>
        <Text className="admin-module-description">
          Manage topic tree structure templates, including creating, editing, and deleting topic nodes and hierarchies. Private topic trees are for private libraries; public topic trees are for collaborative libraries.
        </Text>
      </div>

      {/* 操作工具栏 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Search
            placeholder="Search topic name or description"
            allowClear
            onSearch={handleSearch}
            style={{ width: 300 }}
            enterButton={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add Topic
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleBatchDelete}
            disabled={selectedRowKeys.length === 0}
          >
            Batch Delete ({selectedRowKeys.length})
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchTopics}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </Card>

      {/* 数据表格 */}
      <Card style={{ height: 'calc(100vh - 300px)', overflow: 'hidden' }}>
        <Table
          columns={columns}
          dataSource={topics}
          loading={loading}
          rowKey="tnl_id"
          rowSelection={rowSelection}
          pagination={{
            total: topics.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} records`,
          }}
          scroll={{ x: 1200, y: 'calc(100vh - 400px)' }}
        />
      </Card>

      {/* 添加/编辑模态框 */}
      <Modal
        title={editingTopic ? 'Edit Topic' : 'Add Topic'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        okText="Save"
        cancelText="Cancel"
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            super_tnl_id: null,
            is_private: false,
            is_forever: false,
          }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Topic Name"
                rules={[
                  { required: true, message: 'Please enter topic name!' },
                  { min: 1, max: 100, message: 'Topic name must be 1-100 characters!' }
                ]}
              >
                <Input placeholder="Enter topic name" />
              </Form.Item>

              <Form.Item
                name="super_tnl_id"
                label="Parent Topic"
                help="Select a parent topic, or leave empty for root level"
              >
                <Select
                  placeholder="Select parent topic (optional)"
                  allowClear
                  options={getParentOptions()}
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
                rules={[
                  { required: true, message: 'Please enter topic description!' },
                  { min: 20, message: 'Description must be at least 20 characters!' },
                  { max: 200, message: 'Description cannot exceed 200 characters!' }
                ]}
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Enter topic description (at least 20 characters)"
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="tagnamelist"
                label="Tag List"
                rules={[
                  { required: true, message: 'Please enter tag list!' },
                  { min: 20, message: 'Tag list must be at least 20 characters!' },
                  { max: 500, message: 'Tag list cannot exceed 500 characters!' }
                ]}
              >
                <Input.TextArea
                  rows={12}
                  placeholder="Enter tag list, separated by commas (at least 20 characters)"
                />
              </Form.Item>
              
              {/* AI生成按钮单独放置 */}
              <div style={{ textAlign: 'right', marginBottom: 8 }}>
                <Button
                  type="link"
                  icon={<RobotOutlined />}
                  loading={aiGenerating}
                  onClick={handleAIGenerateTags}
                  style={{ 
                    padding: '0 8px', 
                    height: 'auto',
                    fontSize: '12px'
                  }}
                >
                  AI Generate
                </Button>
              </div>
            </Col>
            
            <Col span={12}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '4px'
                }}>
                  <span style={{ fontWeight: '500', color: 'rgba(0, 0, 0, 0.85)' }}>Private</span>
                  <Form.Item
                    name="is_private"
                    valuePropName="checked"
                    style={{ margin: 0 }}
                  >
                    <Switch
                      checkedChildren="Private"
                      unCheckedChildren="Public"
                    />
                  </Form.Item>
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: 'rgba(0, 0, 0, 0.45)',
                  lineHeight: '1.2',
                  marginTop: '0px'
                }}>
                  Private topics are only visible to the creator; public topics are visible to all users
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '4px'
                }}>
                  <span style={{ fontWeight: '500', color: 'rgba(0, 0, 0, 0.85)' }}>Permanent</span>
                  <Form.Item
                    name="is_forever"
                    valuePropName="checked"
                    style={{ margin: 0 }}
                  >
                    <Switch
                      checkedChildren="Permanent"
                      unCheckedChildren="Normal"
                    />
                  </Form.Item>
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: 'rgba(0, 0, 0, 0.45)',
                  lineHeight: '1.2',
                  marginTop: '0px'
                }}>
                  Permanent topics cannot be deleted once created; normal topics can be deleted
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default TopicTreeManagement; 